import React, { useRef, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";
import { Capacitor } from "@capacitor/core";
import { saveImageToGallery } from "../../../helpers/gallerySave.helper";

const ProfileImage = ({ profile, UPLOAD_URL, getGenderPlaceholder }: any) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const lastDistance = useRef<number | null>(null);
  const lastTouch = useRef<{ x: number; y: number } | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const MIN_SWIPE_DISTANCE = 50;

  const allImages = [
    profile?.image
      ? {
          src: `${UPLOAD_URL}/users/profile_pic/${profile.image}`,
        }
      : null,
    ...(profile?.gallery || []).map((img: string) => ({
      src: `${UPLOAD_URL}/users/gallery/${img}`,
    })),
  ].filter(Boolean) as { src: string }[];

  const currentImageSrc = allImages[currentIndex]?.src || "";
  const blurPhoto = shouldBlurProfileImage(profile?.imagePrivacy);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = getGenderPlaceholder(profile?.gender);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) > MIN_SWIPE_DISTANCE) {
      if (distance > 0) {
        setCurrentIndex((prev) => (prev + 1) % allImages.length);
      } else {
        setCurrentIndex(
          (prev) => (prev - 1 + allImages.length) % allImages.length,
        );
      }
    }
  };

  const goToNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const goToPrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(currentImageSrc);

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const ext = currentImageSrc.split("?")[0].split(".").pop() || "jpg";
      const fileName = `${profile?.martialId || profile?.fullName || "profile"}-${
        currentIndex + 1
      }.${ext}`;

      if (Capacitor.isNativePlatform()) {
        await saveImageToGallery(blob, fileName);
        toast.success("Image saved to gallery");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      toast.success("Image downloaded successfully");
    } catch (error) {
      console.error("Image download failed:", error);
      toast.error("Failed to download image");
    }
  };

  const onModalTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastDistance.current = getTouchDistance(e.touches);
      return;
    }

    if (e.touches.length === 1 && zoom > 1) {
      lastTouch.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      return;
    }

    onTouchStart(e);
  };

  const onModalTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastDistance.current) {
      const currentDistance = getTouchDistance(e.touches);
      const diff = currentDistance - lastDistance.current;

      setZoom((prev) => {
        const next = prev + diff * 0.005;
        return Math.min(Math.max(next, 1), 4);
      });

      lastDistance.current = currentDistance;
      return;
    }

    if (e.touches.length === 1 && zoom > 1 && lastTouch.current) {
      const touch = e.touches[0];

      setPosition((prev) => ({
        x: prev.x + (touch.clientX - lastTouch.current!.x),
        y: prev.y + (touch.clientY - lastTouch.current!.y),
      }));

      lastTouch.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      return;
    }

    onTouchMove(e);
  };

  const onModalTouchEnd = () => {
    lastDistance.current = null;
    lastTouch.current = null;

    if (zoom <= 1) {
      setPosition({ x: 0, y: 0 });
      onTouchEnd();
    }
  };

  return (
    <>
      <div
        className="relative mx-auto md:mx-0 h-96 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer"
        onClick={() => setIsModalOpen(true)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={currentImageSrc}
          alt={profile?.fullName}
          onError={handleImageError}
          className={`absolute inset-0 w-full h-full object-cover blur-lg scale-110`}
        />
        <img
          src={currentImageSrc}
          alt={profile?.fullName}
          onError={handleImageError}
          className={`relative w-full h-full object-cover object-center rounded-lg ${
            blurPhoto ? "blur-sm scale-105 brightness-90" : ""
          }`}
        />
        {allImages.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2
                         bg-black/30 hover:bg-black/60 text-white p-2 rounded-full
                         transition-all"
            >
              <ChevronLeft size={24} />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2
                         bg-black/30 hover:bg-black/60 text-white p-2 rounded-full
                         transition-all"
            >
              <ChevronRight size={24} />
            </button>

            <div className="absolute top-3 right-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
              {currentIndex + 1} / {allImages.length}
            </div>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {allImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(index);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? "bg-white w-6" : "bg-white/60 w-2"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
          <div
            className="
    relative
    w-screen
    h-screen
    flex
    items-center
    justify-center
    overflow-hidden
    bg-black/95
  "
            onTouchStart={onModalTouchStart}
            onTouchMove={onModalTouchMove}
            onTouchEnd={onModalTouchEnd}
          >
            <img
              src={currentImageSrc}
              alt={profile?.fullName}
              onError={handleImageError}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transformOrigin: "center center",
              }}
              className={`
                rounded-xl
                object-contain
                w-full
                h-full
                max-w-[95vw]
                max-h-[calc(95vh-var(--top-safe-area)-var(--bottom-safe-area))]
                transition-transform
                select-none
                ${blurPhoto ? "blur-sm brightness-75" : ""}
              `}
            />

            {allImages.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2
                             bg-white/20 hover:bg-white/40 text-white p-3 rounded-full"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2
                             bg-white/20 hover:bg-white/40 text-white p-3 rounded-full"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}

            <div
              className="absolute right-4 flex gap-3"
              style={{
                top: "calc(var(--top-safe-area) + 1rem)",
              }}
            >
              {/* Desktop zoom buttons only */}
              <div className="hidden sm:flex gap-3">
                <button
                  onClick={() => setZoom((z) => Math.min(z + 0.25, 4))}
                  className="bg-white/20 hover:bg-white/40 text-white p-2 rounded-full"
                >
                  <ZoomIn size={20} />
                </button>

                <button
                  onClick={() => {
                    setZoom((z) => {
                      const next = Math.max(z - 0.25, 1);
                      if (next === 1) setPosition({ x: 0, y: 0 });
                      return next;
                    });
                  }}
                  className="bg-white/20 hover:bg-white/40 text-white p-2 rounded-full"
                >
                  <ZoomOut size={20} />
                </button>
              </div>

              <button
                onClick={downloadImage}
                className="bg-white/20 hover:bg-white/40 text-white p-2 rounded-full"
              >
                <Download size={20} />
              </button>

              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setZoom(1);
                  setPosition({ x: 0, y: 0 });
                }}
                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            {allImages.length > 1 && (
              <div
                className="absolute left-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm"
                style={{
                  top: "calc(var(--top-safe-area) + 1rem)",
                }}
              >
                {currentIndex + 1} / {allImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileImage;
